import {
  Checkbox,
  checkboxStates,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import { modalTypes } from "constants/types";
import { isEnglish } from "layout/documentReadabilityReport/renderers";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal } from "slices/app/actions/modals";
import { requestPoll } from "slices/app/actions/poller";
import { removeErrorMessage } from "slices/app/errorSlice";
import {
  documentReadabilityAnnotatedDocument,
  documentReadabilityExcelReport,
  documentReadabilityPDFReport,
  statusDocumentReadabilityAnnotatedDocument,
  statusDocumentReadabilityExcelReport,
} from "slices/readability/actions/readability";

const options = {
  excel: {
    reportTypeDescription: "Excel report",
    buttonTitle: "Generate Report",
  },
  pdf: {
    reportTypeDescription: "PDF report",
    buttonTitle: "Generate Report",
  },
  "annotated-document": {
    reportTypeDescription: "Word document",
    buttonTitle: "Download Annotated Document",
  },
};

const DocumentReadabilityReportOptionsModal = ({
  reportType,
  documentId,
  folderId,
  title,
  documentInfo,
}) => {
  const dispatch = useDispatch();

  const flesch = isEnglish(documentInfo);

  const { reportTypeDescription, buttonTitle } = options[reportType];

  const [passiveVoice, setPassiveVoice] = useState(flesch ? true : false);
  const [hiddenVerbs, setHiddenVerbs] = useState(flesch ? true : false);
  const [longSentences, setLongSentences] = useState(true);
  const [veryLongSentences, setVeryLongSentences] = useState(true);
  const [longWords, setLongWords] = useState(false);

  const handleClose = () => {
    dispatch(hideModal(modalTypes.DOCUMENT_READABILITY_REPORT_OPTIONS));
  };

  const handleExportSuccess = (job) => {
    handleClose();
    if (job?.data?.jobMetaInfo?.file) {
      window.location = job?.data?.jobMetaInfo?.file;
    }
  };

  const onSubmit = () => {
    const metricsToShow = [];
    if (passiveVoice) {
      metricsToShow.push("Passive");
    }
    if (hiddenVerbs) {
      metricsToShow.push("HiddenVerb");
    }
    if (veryLongSentences) {
      metricsToShow.push("VeryLongSentence");
    }
    if (longSentences) {
      metricsToShow.push("Sentence");
    }
    if (longWords) {
      metricsToShow.push("LongWord");
    }

    if (reportType === "pdf") {
      dispatch(
        documentReadabilityPDFReport({
          folderId,
          documentId,
          documentName: title,
          metricsToShow,
        })
      );
      handleClose();
    } else {
      if (reportType === "excel") {
        dispatch(
          requestPoll(
            documentReadabilityExcelReport({
              folderId,
              documentId,
              documentName: title,
              metricsToShow,
            }),
            statusDocumentReadabilityExcelReport,
            handleExportSuccess,
            (error) => showErrorModal(error),
            <>
              Sorry we can&apos;t export to Excel at this time, please try again
              later
            </>
          )
        );
        handleClose();
      } else {
        dispatch(
          requestPoll(
            documentReadabilityAnnotatedDocument({
              folderId,
              documentId,
              documentName: title,
              metricsToShow,
            }),
            statusDocumentReadabilityAnnotatedDocument,
            handleExportSuccess,
            (error) => showErrorModal(error),
            <>
              Sorry we can&apos;t export to annotated document at this time,
              please try again later
            </>
          )
        );
      }
    }
    dispatch(removeErrorMessage());
  };

  return (
    <Modal
      data-testid="document-readability-report-options-modal"
      className="fade-in document_readability_report_options_modal"
      show
      onDismiss={handleClose}
      backdrop
      centered
    >
      <Modal.Header>
        <b>Readability Report Options</b>
      </Modal.Header>
      <Modal.Body>
        <p>
          We will create a copy of your {reportTypeDescription} with comments
          highlighting all paragraphs that contain Readability issues.
        </p>
        <p>
          You can filter the report output by choosing which metrics to include.
        </p>
        {flesch ? (
          <>
            <div className="field" data-testid="passive-voice-button">
              <span>
                <b>Passive Voice</b>
              </span>
              <Checkbox
                state={
                  passiveVoice ? checkboxStates.checked : checkboxStates.empty
                }
                handleChange={() => {
                  setPassiveVoice(!passiveVoice);
                }}
              />
            </div>
            <div className="field" data-testid="hidden-verbs-button">
              <span>
                <b>Hidden Verbs</b>
              </span>
              <Checkbox
                state={
                  hiddenVerbs ? checkboxStates.checked : checkboxStates.empty
                }
                handleChange={() => {
                  setHiddenVerbs(!hiddenVerbs);
                }}
              />
            </div>
          </>
        ) : null}
        <div className="field" data-testid="long-sentences-button">
          <span>
            <b>Long Sentences</b>
          </span>
          <Checkbox
            state={
              longSentences ? checkboxStates.checked : checkboxStates.empty
            }
            handleChange={() => {
              setLongSentences(!longSentences);
            }}
          />
        </div>
        <div className="field" data-testid="very-long-sentences-button">
          <span>
            <b>Very Long Sentences</b>
          </span>
          <Checkbox
            state={
              veryLongSentences ? checkboxStates.checked : checkboxStates.empty
            }
            handleChange={() => {
              setVeryLongSentences(!veryLongSentences);
            }}
          />
        </div>
        <div className="field" data-testid="long-words-button">
          <span>
            <b>Long Words</b>
          </span>
          <Checkbox
            state={longWords ? checkboxStates.checked : checkboxStates.empty}
            handleChange={() => {
              setLongWords(!longWords);
            }}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-testid="cancel-button"
          variant={NewButtonVariant.l2}
          onClick={handleClose}
        >
          Cancel
        </NewButton>

        <NewButton
          variant={NewButtonVariant.l1}
          onClick={onSubmit}
          data-testid="ok-button"
        >
          {buttonTitle}
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default DocumentReadabilityReportOptionsModal;

DocumentReadabilityReportOptionsModal.propTypes = {
  reportType: PropTypes.string.isRequired,
  documentId: PropTypes.string.isRequired,
  folderId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,

  documentInfo: PropTypes.any.isRequired,
};
